import React from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    Label,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    UncontrolledPopover,
    PopoverBody,
    Badge,
} from "reactstrap";
import moment from "moment";

interface Attendee {
    email: string;
    responseStatus: string | null;
}

interface EventDetails {
    id: string;
    title?: string;
    formattedDate?: string;
    formattedTime?: string;
    start?: string;
    end?: string;
    attendees?: Attendee[];
    locationType?: string;
    address?: string;
    meetingLink?: string;
    description?: string;
    status?: string;
    source?: string;
}

interface StatusOption {
    value: string;
    label: string;
}

interface Props {
    isOpen: boolean;
    toggle: () => void;
    event: EventDetails | null;
    dropdownOpen: boolean;
    toggleDropdown: () => void;
    statusOptions: StatusOption[];
    handleStatusUpdate: (eventId: string, status: StatusOption) => void;
    getStatusColor: (status: string) => string;
    formatResponseStatus: (status: string) => string;
}

const EventDetailsModal: React.FC<Props> = ({
    isOpen,
    toggle,
    event,
    dropdownOpen,
    toggleDropdown,
    statusOptions,
    handleStatusUpdate,
    getStatusColor,
    formatResponseStatus,
}) => {

    function cleanDescriptionHTML(html: string): string {
        const div = document.createElement("div");
        div.innerHTML = html;

        // Remove consecutive <br> tags (e.g., more than 2)
        let htmlString = div.innerHTML.replace(/(<br\s*\/?>\s*){3,}/gi, "<br><br>");

        return htmlString.trim();
    }

    console.log("event details", event)
    if (!event) return null;
    return (
        <Modal isOpen={isOpen} id="event-modal" centered>
            <ModalHeader
                toggle={toggle}
                tag="h5"
                className="p-3 bg-primary d-flex justify-content-between align-items-center"
            >
                <span className="text-light">
                    <i className="ri-calendar-event-line me-2" />
                    {event.title || "Event Details"}
                </span>
            </ModalHeader>

            <ModalBody>
                <div className="d-flex flex-column gap-3">

                    <section>
                        <h6 className="text-muted mb-2">Event Info</h6>
                        <div className="d-flex mb-2">
                            <strong className="me-2">Name:</strong>
                            <span>{event.title || "N/A"}</span>
                        </div>
                        <div className="d-flex mb-2">
                            <strong className="me-2">Date:</strong>
                            <span>{event.formattedDate || moment(event.start).format("DD MMM YYYY")}</span>
                        </div>
                        <div className="d-flex mb-2">
                            <strong className="me-2">Time:</strong>
                            <span>{event.formattedTime || `${moment(event.start).format("hh:mm A")} - ${moment(event.end).format("hh:mm A")}`}</span>
                        </div>
                    </section>

                    <section>
                        <h6 className="text-muted mb-2">Location</h6>
                        <div className="d-flex mb-2">
                            <strong className="me-2">Type:</strong>
                            <span>{event.locationType?.toUpperCase() || "N/A"}</span>
                        </div>
                        {event.address && (
                            <div className="d-flex mb-2">
                                <strong className="me-2">Address:</strong>
                                <span>{event.address}</span>
                            </div>
                        )}
                        <div className="d-flex mb-2 align-items-center">
                            <strong className="me-2">Link:</strong>
                            <span>
                                {event.meetingLink ? (
                                    <a href={event.meetingLink} target="_blank" rel="noopener noreferrer">
                                        {event.meetingLink} <i className="ri-external-link-line ms-1" />
                                    </a>
                                ) : (
                                    "Not Provided"
                                )}
                            </span>
                        </div>
                    </section>

                    <div className="mb-2">
                        <h6 className="text-muted">Details</h6>
                        <div
                            className="overflow-hidden"
                            dangerouslySetInnerHTML={{
                                __html: cleanDescriptionHTML(event.description || "")
                            }}
                        ></div>
                        <div className="d-flex align-items-center">
                            <strong className="me-2">Status:</strong>
                            {event?.source === "internal" ? (
                                <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
                                    <DropdownToggle color="primary" size="sm">
                                        <i className="ri-edit-2-line me-1" />
                                        {event.status
                                            ? event.status.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
                                            : "Update Status"}
                                    </DropdownToggle>
                                    <DropdownMenu>
                                        {statusOptions.map((status) => (
                                            <DropdownItem key={status.value} onClick={() => handleStatusUpdate(event.id, status)}>
                                                {status.label}
                                            </DropdownItem>
                                        ))}
                                    </DropdownMenu>
                                </Dropdown>
                            ) : (
                                <div className="d-flex align-items-center">
                                    <span><Badge color="light" className="text-body"> {event.status
                                            ? event.status.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
                                            : "No Status"} </Badge></span>
                                    <i
                                        className="ri-information-line ms-2 text-muted"
                                        id="StatusInfoIcon"
                                        style={{ cursor: "pointer" }}
                                    ></i>
                                    <UncontrolledPopover
                                        placement="top"
                                        target="StatusInfoIcon"
                                        trigger="hover"
                                    >
                                        <PopoverBody>
                                            This is an external event. Status cannot be changed.
                                        </PopoverBody>
                                    </UncontrolledPopover>
                                </div>
                            )}
                        </div>


                    </div>

                    <hr />

                    <section>
                        <h6 className="text-muted mb-2">Attendees</h6>
                        {event.attendees?.length ? (
                            <div style={{ maxHeight: "120px", overflowY: "auto", padding: "8px", borderRadius: "6px" }}>
                                {event.attendees.map((attendee, index) => (
                                    <div key={index} className="d-flex justify-content-between align-items-center mb-1" style={{ fontSize: "13px" }}>
                                        <Label className="text-muted mb-0">
                                            <i className="ri-user-fill me-1" />
                                            {attendee.email}
                                        </Label>
                                        {attendee.responseStatus && (
                                            <span
                                                className="badge rounded-pill bg-info-subtle"
                                                style={{ color: getStatusColor(attendee.responseStatus) }}
                                            >
                                                {formatResponseStatus(
                                                    attendee.responseStatus
                                                        ? attendee.responseStatus.charAt(0).toUpperCase() + attendee.responseStatus.slice(1).toLowerCase()
                                                        : ""
                                                )}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-muted">No Attendees</div>
                        )}
                    </section>
                </div>
            </ModalBody>
        </Modal>
    );
};

export default EventDetailsModal;